import moment from 'moment';
import { useState } from 'react';
import { CARD_ASTRA_STATUS, CARD_FRIENDLY_STATUS } from '../../../../../constants/consumers';
import classNames from 'classnames';
// icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
// API
import { useEnv } from '../../../../../context/env-context';
import { useAuth } from '../../../../../hooks/useAuth';
import { makeRequest } from '../../../../../utils/makeRequest';
import { toast } from 'react-toastify';
// components
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import CardProviderImage from '../../../../../components/CardProviderImage';
import ConsumerPopoverNewCardForm from '../forms/ConsumerPopoverNewCardForm';

function ConsumerAstraCardsSection({
  astraUser,
  setSpinner,
  getAstraUser,
  sendCardRequest,
  getCards,
  getBankAccountValue,
  cardsBlockStatus,
  getCardsBlockStatus,
  unblockLinkStatus,
  consumerId,
  consumerFirstName,
  consumerLastName,
  consumerAddressLine1,
  consumerAddressLine2,
  consumerCity,
  consumerState,
  consumerZip,
}) {
  const [showLog, setShowLog] = useState([]);

  const { apiOriginConsumer } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const createAstraUser = async () => {
    setSpinner(true);

    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginConsumer}/astra/create_user`,
      method: 'POST',
      data: JSON.stringify({ consumerId }),
    };

    try {
      await makeRequest(config);
      await getAstraUser();
      toast.success('Astra user has been created');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
    }
  };

  const deleteAstraUser = async () => {
    if (window.confirm('Are you sure?')) {
      setSpinner(true);

      const token = await getAccessTokenSilently();

      if (!token) {
        return;
      }

      const config = {
        token,
        url: `${apiOriginConsumer}/astra/delete_user`,
        method: 'POST',
        data: JSON.stringify({ consumerId }),
      };

      try {
        const response = await makeRequest(config);

        window.alert(
          `Astra user data has been removed from consumer ${consumerId}. ` +
            'Please delete the user manually in Astra dashboard. ' +
            `Astra user id: ${response.astraUserId}`,
        );
        await getAstraUser();
      } catch (error) {
        toast.error(error.message);
      } finally {
        setSpinner(false);
      }
    }
  };

  const migrateCards = async () => {
    setSpinner(true);

    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginConsumer}/cards/migrate`,
      method: 'POST',
      data: JSON.stringify({ consumerId }),
    };

    try {
      await makeRequest(config);
      await getAstraUser();
      await getCards();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center gap-1">
        <Button className="bo-common-button px-2" onClick={createAstraUser}>
          <p className="bo-common-text-sm">Create User</p>
        </Button>
        <Button className="bo-common-button px-2" onClick={deleteAstraUser}>
          <p className="bo-common-text-sm">Delete User</p>
        </Button>
        <Button className="bo-common-button px-2" onClick={migrateCards}>
          <p className="bo-common-text-sm">Migrate Cards</p>
        </Button>
      </div>
      <div>
        <Form.Label className="bo-common-input-label mt-3">Astra User Id:</Form.Label>
        <p className="bo-common-text text-truncate">
          {astraUser?.userId ? astraUser.userId : 'N/A'}
        </p>
        <Form.Label className="bo-common-input-label mt-3">Astra User Status:</Form.Label>
        <p className="bo-common-text text-capitalize">
          {astraUser?.status ? astraUser.status : 'N/A'}
        </p>
      </div>
      <div className="mt-3">
        {astraUser?.cards.map(card => (
          <Card
            className={classNames(
              'mb-3 p-3 bo-max-w-400 overflow-hidden',
              card.status === 'ADHOC_ONLY' && 'bo-card-adhoc-bg-color',
            )}
            key={card.id}
          >
            <div className="d-flex justify-content-between align-items-center">
              <CardProviderImage height={12} company={card.cardCompany} />
              <div className="d-flex align-items-center">
                <p className="bo-common-text me-1">{card.cardType?.toUpperCase()}</p>
                {card.isMain && <FontAwesomeIcon icon={faStar} size="sm" color="orange" />}
              </div>
            </div>
            <div className="mt-3">
              {card.tpCardId && (
                <p className="bo-common-text-bold bo-text-red mb-1">
                  This card will be processed via TabaPay
                </p>
              )}
              <div className="d-flex mb-1 align-items-center">
                <p className="bo-common-text bo-w-150">Card Number:</p>
                <p className="bo-common-text text-nowrap">
                  {`**** ***** **** ${card.lastFourDigits}`}
                </p>
              </div>
              <div className="d-flex mb-1 align-items-center">
                <p className="bo-common-text bo-w-150">EXP:</p>
                <p className={`bo-common-text ${card.isExpired ? 'bo-text-red' : ''}`}>
                  {`${card.expirationDate} ${card.isExpired ? 'Expired!' : ''}`}
                </p>
              </div>
              <div className="d-flex mb-1 align-items-center">
                <p className="bo-common-text bo-min-w-150">Card Holder:</p>
                <p className="bo-common-text text-truncate">
                  {`${card.firstName} ${card.lastName}`}
                </p>
              </div>
              <hr className="my-2" />
              <div className="d-flex mb-1 align-items-center">
                <p className="bo-common-text bo-w-150">Astra Status:</p>
                <Badge
                  className="bo-common-inline-badge text-capitalize"
                  bg={CARD_ASTRA_STATUS[card.astraStatus]?.color || 'danger'}
                >
                  {CARD_ASTRA_STATUS[card.astraStatus]?.text}
                </Badge>
              </div>
              <div className="d-flex mb-1 align-items-center">
                <p className="bo-common-text bo-w-150">Push enabled:</p>
                <p className="bo-common-text">{card.pushEnabled ? 'Yes' : 'No'}</p>
              </div>
              <div className="d-flex mb-1 align-items-center">
                <p className="bo-common-text bo-w-150">Pull enabled:</p>
                <p className="bo-common-text">{card.pullEnabled ? 'Yes' : 'No'}</p>
              </div>
              <div className="d-flex mb-1 align-items-top overflow-hidden">
                <p className="bo-common-text bo-min-w-150">Status:</p>
                <div className="d-flex flex-wrap align-items-center gap-1">
                  <Badge
                    className="bo-common-inline-badge text-truncate"
                    bg={CARD_FRIENDLY_STATUS[card.friendlyStatus]?.color}
                  >
                    {CARD_FRIENDLY_STATUS[card.friendlyStatus]?.text}
                  </Badge>
                  <Button
                    className="bo-common-inline-button px-1 py-0 text-nowrap"
                    onClick={() =>
                      sendCardRequest({
                        url: card.friendlyStatus === 'BLOCKED' ? 'unblock' : 'block',
                        data: {
                          cardId: card.id,
                          isAstra:
                            card.friendlyStatus === 'BLOCKED' && !card.tpCardId ? true : undefined,
                        },
                      })
                    }
                  >
                    {card.friendlyStatus === 'BLOCKED' ? 'Unblock' : 'Block'}
                  </Button>
                </div>
              </div>
              <hr className="my-2" />
              <div className="d-flex mb-1 align-items-center">
                <p className="bo-common-text bo-min-w-150">Bank Account:</p>
                {getBankAccountValue(card)}
              </div>
            </div>
            <div className="mt-2 d-flex align-items-center gap-2">
              <div className="d-flex bo-w-150">
                {card.status !== 'ADHOC_ONLY' && (
                  <Button
                    className="bo-common-inline-button"
                    onClick={() =>
                      sendCardRequest({ url: 'set_main', data: { consumerId, cardId: card.id } })
                    }
                    disabled={card.isMain}
                  >
                    Set as main card
                  </Button>
                )}
              </div>
              <div className="d-flex flex-grow-1 justify-content-between gap-2">
                <Button
                  className="bo-common-inline-button text-nowrap"
                  onClick={() =>
                    setShowLog(prev =>
                      prev.includes(card.id) ? prev.filter(x => x !== card.id) : [...prev, card.id],
                    )
                  }
                >
                  {showLog.includes(card.id) ? 'Hide Log' : 'Show Log'}
                </Button>
                <Button
                  className="bo-common-inline-button"
                  onClick={() =>
                    sendCardRequest({
                      url: 'remove',
                      data: { consumerId, cardId: card.id },
                      askForConfirmation: true,
                    })
                  }
                >
                  Delete
                </Button>
              </div>
            </div>
            {showLog.includes(card.id) && (
              <div>
                <hr className="my-2" />
                {card.log
                  .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                  .map((x, idx) => (
                    <div
                      key={x.id}
                      className={`d-flex align-items-center ${idx % 2 !== 0 ? 'bg-light' : ''}`}
                    >
                      <div className="bo-min-w-150">
                        <p className="bo-common-text">{moment(x.timestamp).format('YYYY-MM-DD')}</p>
                        <p className="bo-common-text">{moment(x.timestamp).format('HH:mm:ss')}</p>
                      </div>
                      <p className="bo-common-text">{x.details}</p>
                    </div>
                  ))}
              </div>
            )}
          </Card>
        ))}
      </div>
      {astraUser?.userId && (
        <ConsumerPopoverNewCardForm
          getAstraUser={getAstraUser}
          getCards={getCards}
          cardsBlockStatus={cardsBlockStatus}
          getCardsBlockStatus={getCardsBlockStatus}
          consumerId={consumerId}
          consumerFirstName={consumerFirstName}
          consumerLastName={consumerLastName}
          consumerAddressLine1={consumerAddressLine1}
          consumerAddressLine2={consumerAddressLine2}
          consumerCity={consumerCity}
          consumerState={consumerState}
          consumerZip={consumerZip}
        />
      )}
      {cardsBlockStatus.blocked && (
        <div className="d-flex align-items-end gap-2 mt-2">
          <p className="bo-common-text-sm text-danger text-nowrap text-truncate">
            Blocked for linking a new card until{' '}
            {moment.utc(cardsBlockStatus.blockedUntil).local().format('h:mm a')}
          </p>
          <Button className="bo-common-inline-button text-nowrap" onClick={unblockLinkStatus}>
            <p className="bo-common-text-bold">Unblock</p>
          </Button>
        </div>
      )}
    </div>
  );
}

export default ConsumerAstraCardsSection;
